import React, { FC, useMemo } from 'react';
import { t } from '@lingui/core/macro';
import { useLingui } from '@lingui/react';
import { Flex, Paragraph, Svg } from '@wunder/ui-components';
import { Subscription } from '@/features/subscriptions/types/Subscription.types';
import { replaceUnderscoresAndCapitalize } from '@/core/utils/replaceUnderscoresAndCapitalize';
import styles from '@/features/subscriptions/assets/scss/SubscriptionTile.module.scss';

interface SubscriptionBenefitsProps {
  subscription: Subscription;
  className?: string;
  benefitTextClassName?: string;
}

/**
 * Component that formats and displays subscription benefits
 * Extracts all benefit information directly from the subscription object
 */
const SubscriptionBenefits: FC<SubscriptionBenefitsProps> = ({
  subscription,
  className,
  benefitTextClassName,
}) => {
  const { i18n } = useLingui();

  const benefits = useMemo(() => {
    const formattedBenefits: Array<{ id: string; text: string; icon?: string }> = [];

    const { currency = '€', details } = subscription;

    // Properly localized currency formatter using i18n
    const formatCurrency = (value: number, curr: string) => {
      return i18n.number(value, {
        style: 'currency',
        currency: curr,
      });
    };

    const applicableRentalCount = subscription.numberOfRentals;
    const isUnlimited = applicableRentalCount === undefined || applicableRentalCount === null;

    // Check if monetary or time-based
    if (subscription.value !== undefined && subscription.value > 0) {
      // If monetary is false, value represents free minutes
      if (subscription.monetary === false) {
        formattedBenefits.push({
          id: 'freeMinutes',
          text: t({
            id: 'subscriptions.benefits.freeMinutes',
            message: `${subscription.value} free minutes`,
          }),
          icon: 'clock',
        });
      } else {
        let valueText: string;

        if (isUnlimited) {
          valueText = t({
            id: 'subscriptions.benefits.creditValueUnlimited',
            message: `${formatCurrency(subscription.value, currency)} credit included`,
          });
        } else if (applicableRentalCount) {
          valueText = t({
            id: 'subscriptions.benefits.creditValueWithRentals',
            message: `${formatCurrency(subscription.value, currency)} credit included\n(for ${applicableRentalCount} rides)`,
          });
        } else {
          valueText = t({
            id: 'subscriptions.benefits.creditValue',
            message: `${formatCurrency(subscription.value, currency)} credit included`,
          });
        }

        formattedBenefits.push({
          id: 'value',
          text: valueText,
          icon: 'wallet',
        });
      }
    }

    if (subscription.discount !== undefined && subscription.discount > 0) {
      const discountPercent = Math.round(subscription.discount * 100);
      let discountText: string;

      if (isUnlimited) {
        discountText = t({
          id: 'subscriptions.benefits.discountOnRidesUnlimited',
          message: `${discountPercent}% discount on rides`,
        });
      } else if (applicableRentalCount) {
        discountText = t({
          id: 'subscriptions.benefits.discountOnRidesWithRentals',
          message: `${discountPercent}% discount on rides\n(for ${applicableRentalCount} rides)`,
        });
      } else {
        discountText = t({
          id: 'subscriptions.benefits.discountOnRides',
          message: `${discountPercent}% discount on rides`,
        });
      }

      formattedBenefits.push({
        id: 'discount',
        text: discountText,
        icon: 'percent',
      });
    }

    // Rental rebate (monetary amount deducted per rental)
    if (subscription.rentalRebate !== undefined && subscription.rentalRebate > 0) {
      let rebateText: string;

      if (isUnlimited) {
        rebateText = t({
          id: 'subscriptions.benefits.rentalRebateUnlimited',
          message: `${formatCurrency(subscription.rentalRebate, currency)} rebate per ride`,
        });
      } else if (applicableRentalCount) {
        rebateText = t({
          id: 'subscriptions.benefits.rentalRebateWithRentals',
          message: `${formatCurrency(subscription.rentalRebate, currency)} rebate per ride\n(for ${applicableRentalCount} rides)`,
        });
      } else {
        rebateText = t({
          id: 'subscriptions.benefits.rentalRebate',
          message: `${formatCurrency(subscription.rentalRebate, currency)} rebate per ride`,
        });
      }

      formattedBenefits.push({
        id: 'rentalRebate',
        text: rebateText,
        icon: 'wallet',
      });
    }

    // Details object properties (optional, contains special benefits like free unlocks, package discounts)
    if (details) {
      // Percentage discount per rental (from details)
      if (details.percentageDiscount !== undefined && details.percentageDiscount > 0) {
        const discountPercent = Math.round(details.percentageDiscount * 100);
        let discountText: string;
        if (isUnlimited) {
          discountText = t({
            id: 'subscriptions.benefits.discountOnRidesUnlimited',
            message: `${discountPercent}% discount on rides`,
          });
        } else if (applicableRentalCount) {
          discountText = t({
            id: 'subscriptions.benefits.discountOnRidesWithRentals',
            message: `${discountPercent}% discount on rides\n(for ${applicableRentalCount} rides)`,
          });
        } else {
          discountText = t({
            id: 'subscriptions.benefits.discountOnRides',
            message: `${discountPercent}% discount on rides`,
          });
        }

        formattedBenefits.push({
          id: 'percentageDiscount',
          text: discountText,
          icon: 'percent',
        });
      }

      // Absolute discount per rental (from details)
      if (details.absoluteDiscount !== undefined && details.absoluteDiscount > 0) {
        let discountText: string;
        if (isUnlimited) {
          discountText = t({
            id: 'subscriptions.benefits.absoluteDiscountOnRidesUnlimited',
            message: `${formatCurrency(details.absoluteDiscount, currency)} discount per ride`,
          });
        } else if (applicableRentalCount) {
          discountText = t({
            id: 'subscriptions.benefits.absoluteDiscountOnRidesWithRentals',
            message: `${formatCurrency(details.absoluteDiscount, currency)} discount per ride\n(for ${applicableRentalCount} rides)`,
          });
        } else {
          discountText = t({
            id: 'subscriptions.benefits.absoluteDiscountOnRides',
            message: `${formatCurrency(details.absoluteDiscount, currency)} discount per ride`,
          });
        }

        formattedBenefits.push({
          id: 'absoluteDiscount',
          text: discountText,
          icon: 'discount',
        });
      }

      // Unlimited unlocks
      if (details.freeUnlocks) {
        formattedBenefits.push({
          id: 'freeUnlocks',
          text: t({
            id: 'subscriptions.benefits.freeUnlocks',
            message: 'Unlimited free unlocks',
          }),
          icon: 'unlock',
        });
      }

      // Discount on package purchase
      const packageDiscount =
        typeof details.discountOnPackages === 'string'
          ? Number.parseFloat(details.discountOnPackages)
          : details.discountOnPackages;
      if (packageDiscount !== undefined && packageDiscount > 0) {
        formattedBenefits.push({
          id: 'discountOnPackages',
          text: t({
            id: 'subscriptions.benefits.discountOnPackages',
            message: `${Math.round(packageDiscount * 100)}% discount on all packages`,
          }),
          icon: 'gift',
        });
      }

      // Included additions (e.g., insurance)
      if (details.includedAdditions && details.includedAdditions.length > 0) {
        details.includedAdditions.forEach((addition, index) => {
          formattedBenefits.push({
            id: `addition-${index}`,
            text: t({
              id: 'subscriptions.benefits.includedAddition',
              message: `${addition} included`,
            }),
            icon: 'checkCircle',
          });
        });
      }
    }

    // Entitled addition (e.g. REDUKTION_DES_SELBSTBEHALTS) – fixed copy
    if (subscription.additionEntitlementCode) {
      formattedBenefits.push({
        id: `entitledAddition-${subscription.additionEntitlementCode}`,
        text: t({
          id: 'subscriptions.benefits.entitledAddition',
          message:
            'Excess reduced to €800 per journey (regardless of the vehicle category booked). Replaces the safety package per journey. Valid subject to compliance with the Rail&Drive terms and conditions. Minimum duration: 1 year.',
        }),
        icon: 'gift',
      });
    }

    return formattedBenefits;
  }, [subscription, i18n]);

  return (
    <Flex direction="column" className={className || styles['subscription-tile__benefits']}>
      {benefits.map((benefit) => {
        return (
          <Flex key={benefit.id} gap="md" align="center">
            <span className={styles['subscription-tile__benefit-icon']}>
              <Svg icon="checkmark" width={16} height={16} />
            </span>
            <Paragraph
              size={3}
              marginBottom={0}
              className={benefitTextClassName || styles['subscription-tile__benefit-text']}
            >
              {benefit.text}
            </Paragraph>
          </Flex>
        );
      })}
    </Flex>
  );
};

export default SubscriptionBenefits;
